import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import { fetchCustomerOrganisations } from '../../../../services/organisations';
import { getCurrentUser } from '../../../../services/auth';
import { sendDocumentReportEmail, listMissingDocuments, fetchMissingDocumentsSummary, exportMissingDocumentsCsv } from '../../../../services/reports';
import apiClient from '../../../../services/apiClient';

// Document types for Missing Documents
const documentTypes = [
  'ALL',
  'PASSPORT',
  'VISA_PAGE',
  'EMIRATES_ID',
  'PHOTO',
  'OHC',
  'DRIVING_LICENSE',
];

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

// Map frontend document type to API format
const mapTypeToApi = (t) => {
  switch (t) {
    case 'PASSPORT':
      return 'passport_copy';
    case 'VISA_PAGE':
      return 'visa_page';
    case 'EMIRATES_ID':
      return 'emirates_id';
    case 'PHOTO':
      return 'photo';
    case 'OHC':
      return 'ohc';
    case 'DRIVING_LICENSE':
      return 'driving_license';
    default:
      return t;
  }
};

// Pretty labels for document types
const labelForType = (t) => {
  switch (t) {
    case 'ALL':
      return 'All';
    case 'PASSPORT_COPY':
    case 'PASSPORT':
      return 'Passport';
    case 'VISA_PAGE':
    case 'VISA':
      return 'Visa';
    case 'EMIRATES_ID':
      return 'Emirates ID';
    case 'PHOTO':
      return 'Photo';
    case 'OHC':
      return 'OHC';
    case 'DRIVING_LICENSE':
      return 'Driving License';
    default:
      return t;
  }
};

function SummaryTile({ title, value, color = '#1976d2', valueSx }) {
  return (
    <Box
      sx={{
        flex: 1,
        p: 2,
        borderRadius: 2,
        bgcolor: '#fff',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        position: 'relative',
        overflow: 'hidden',
        transition: 'box-shadow 120ms ease, transform 120ms ease',
        '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.08)', transform: 'translateY(-1px)' },
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          bgcolor: color,
          opacity: 0.9,
        },
      }}
    >
      <Typography variant="body2" sx={{ color: '#334155', fontWeight: 600, mb: 0.5 }}>
        {title}
      </Typography>
      <Typography
        variant="h5"
        sx={{
          color,
          fontWeight: 800,
          lineHeight: 1.2,
          ...(valueSx || {}),
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default function MissingDocumentsPage() {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);
  const [totalElements, setTotalElements] = useState(0);

  const [organizationId, setOrganizationId] = useState('');
  const [selectedType, setSelectedType] = useState('ALL');
  const [summary, setSummary] = useState(null);
  const [orgOptions, setOrgOptions] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [lockOrg, setLockOrg] = useState(false);
  const [userTypeState, setUserTypeState] = useState('');
  const [isRootAdmin, setIsRootAdmin] = useState(false);
  const [orgLabel, setOrgLabel] = useState('');

  // Email dialog state
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminOptions, setAdminOptions] = useState([]); // [{id, name, email}]
  const [selectedAdminIds, setSelectedAdminIds] = useState(new Set());

  const fetchOrgs = async () => {
    setOrgLoading(true);
    try {
      const list = await fetchCustomerOrganisations();
      setOrgOptions(list);
    } catch (e) {
      console.error('Failed to load organizations', e);
      setOrgOptions([]);
    } finally {
      setOrgLoading(false);
    }
  };

  const params = useMemo(() => {
    const p = { page, size };
    if (organizationId) p.organizationId = organizationId;
    if (selectedType && selectedType !== 'ALL') {
      p.documentType = mapTypeToApi(selectedType);
    }
    try { console.debug('[MissingDocs] list params', p); } catch {}
    return p;
  }, [page, size, organizationId, selectedType]);

  const summaryParams = useMemo(() => {
    const p = {};
    if (organizationId) p.organizationId = organizationId;
    if (selectedType && selectedType !== 'ALL') {
      p.documentType = mapTypeToApi(selectedType);
    }
    try { console.debug('[MissingDocs] summary params', p); } catch {}
    return p;
  }, [organizationId, selectedType]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await listMissingDocuments(params);
      setRows(data?.content || []);
      setTotalElements(data?.totalElements || 0);
    } catch (e) {
      console.error('Failed to load missing documents', e);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const { data } = await fetchMissingDocumentsSummary(summaryParams);
      setSummary(data || null);
    } catch (e) {
      console.error('Failed to load missing documents summary', e);
      setSummary(null);
    }
  };

  useEffect(() => {
    // Detect role and lock organization for customer org users or customer admins
    const init = async () => {
      try {
        const user = await getCurrentUser();
        const roles = (user?.roles || []).map((r) => String(r).toLowerCase());
        const userType = String(user?.userType || user?.principalInfo?.userType || '').toUpperCase();
        const isCustomerOrgUser = userType === 'CUSTOMER_ORG';
        const isCustomerAdmin = roles.some((r) => r.includes('customer') && r.includes('admin'));
        const isRootAdminUser = roles.includes('role_root_admin');
        const orgId = user?.organization?.id;
        const orgName = user?.organization?.name;
        const willLock = !!((isCustomerOrgUser || isCustomerAdmin) && orgId);
        if (willLock) {
          setOrganizationId(orgId);
          setLockOrg(true);
          setOrgLabel(orgName || 'My Organization');
        }
        setUserTypeState(userType);
        setIsRootAdmin(isRootAdminUser);
      } catch (e) {
        // ignore auth failures; default to admin behavior
      } finally {
        // Load orgs as applicable
        const user = await getCurrentUser().catch(() => null);
        const roles = (user?.roles || []).map((r) => String(r).toLowerCase());
        const userType = String(user?.userType || user?.principalInfo?.userType || '').toUpperCase();
        const isCustomerOrgUser = userType === 'CUSTOMER_ORG';
        const isCustomerAdmin = roles.some((r) => r.includes('customer') && r.includes('admin'));
        const willLock = !!((isCustomerOrgUser || isCustomerAdmin) && user?.organization?.id);
        if (!willLock) {
          fetchOrgs();
        }
        setUserTypeState(userType);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryParams]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const exportParams = {};
      if (organizationId) exportParams.organizationId = organizationId;
      if (selectedType && selectedType !== 'ALL') {
        exportParams.documentType = mapTypeToApi(selectedType);
      }
      const { blob } = await exportMissingDocumentsCsv(exportParams);
      downloadBlob(blob, `missing-documents-report-${new Date().toISOString().slice(0,10)}.csv`);
    } catch (e) {
      console.error('Export failed', e);
    } finally {
      setExporting(false);
    }
  };

  const handleTypesChange = (event) => {
    setSelectedType(event.target.value);
  };

  // When opening email dialog, load admins for current org
  useEffect(() => {
    if (emailOpen) {
      fetchOrgAdmins();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailOpen, organizationId]);

  const toggleAdminSelection = (id) => {
    setSelectedAdminIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const fetchOrgAdmins = async () => {
    if (!organizationId) {
      setAdminOptions([]);
      setSelectedAdminIds(new Set());
      return;
    }
    setAdminLoading(true);
    try {
      const qp = new URLSearchParams();
      qp.append('organisationId', organizationId);
      const url = `/api/organisations/details?${qp.toString()}`;
      const { data } = await apiClient.get(url);
      const email = data?.email || '';
      const name = data?.name || data?.contactPerson || 'Organization';
      const mapped = email
        ? [{ id: data?.id || organizationId, name, email }]
        : [];
      setAdminOptions(mapped);
      setSelectedAdminIds(new Set(mapped.map((m) => m.id)));
    } catch (e) {
      console.error('Failed to fetch organization admins', e);
      setAdminOptions([]);
      setSelectedAdminIds(new Set());
    } finally {
      setAdminLoading(false);
    }
  };

  const effectiveTypes = useMemo(() => {
    if (selectedType === 'ALL') return [];
    return [selectedType];
  }, [selectedType]);

  // For missing documents email, just send the API format
  const expandTypesForQuery = (selected) => {
    return (selected || []).map(t => mapTypeToApi(t));
  };

  const handleEmailReport = async () => {
    setSendingEmail(true);
    try {
      const selectedEmails = adminOptions.filter(a => selectedAdminIds.has(a.id)).map(a => a.email).filter(Boolean);
      const additional = emailRecipients;
      const recipients = [
        ...selectedEmails,
        ...String(additional || '')
          .split(',')
          .map((e) => e.trim())
          .filter((e) => !!e)
      ].join(',');
      
      const payload = {
        recipients,
        summaryOnly: false,
        documentTypes: expandTypesForQuery(effectiveTypes),
        status: 'CURRENT',
        reportType: 'MISSING', // Extra field for missing documents
      };
      // Add organizationId if available
      if (organizationId) {
        payload.organizationId = organizationId;
      }
      
      await sendDocumentReportEmail(payload);
      setEmailOpen(false);
      setEmailRecipients('');
      setSelectedAdminIds(new Set());
    } catch (e) {
      const status = e?.response?.status;
      const data = e?.response?.data;
      const msg = e?.message || 'Unknown error';
      console.error('Email report failed', { status, data, msg, error: e });
    } finally {
      setSendingEmail(false);
    }
  };

  // Compute whether to hide Organization selector for Customer Org users/admins
  let shouldHideOrgSelector = false;
  try {
    const uiUserType = String(userTypeState || '').toUpperCase();
    const authInfoRaw = localStorage.getItem('authInfo');
    const authInfo = authInfoRaw ? JSON.parse(authInfoRaw) : {};
    const rolesSrc = authInfo?.roles || authInfo?.user?.roles || [];
    const rolesLower = (rolesSrc || []).map((r) => String(r).toLowerCase());
    const userTypeImmediate = String(
      authInfo?.userType || authInfo?.user?.userType || authInfo?.principalInfo?.userType || ''
    ).toUpperCase();
    const hasOrgImmediate = !!(
      authInfo?.organization?.id || authInfo?.user?.organization?.id || localStorage.getItem('organizationCode')
    );
    const isCustomerImmediate =
      rolesLower.some((r) => r.includes('customer')) || userTypeImmediate.includes('CUSTOMER');
    const isCustomerOrgImmediate = userTypeImmediate.includes('CUSTOMER') && userTypeImmediate.includes('ORG');
    const isCustomerAdminImmediate = rolesLower.some(
      (r) => r.includes('customer_admin') || (r.includes('customer') && r.includes('admin'))
    );

    shouldHideOrgSelector =
      lockOrg ||
      uiUserType === 'CUSTOMER_ORG' ||
      isCustomerOrgImmediate ||
      (isCustomerAdminImmediate && hasOrgImmediate) ||
      (isCustomerImmediate && hasOrgImmediate);
  } catch (e) {
    // ignore parse errors; default will be false
  }

  return (
    <Stack spacing={2} sx={{ mb: 1 }}>
      <Typography
        variant="h6"
        sx={{
          fontSize: 18,
          fontWeight: 700,
          color: '#0f172a',
          display: 'flex',
          alignItems: 'center',
          '&::before': {
            content: '""',
            display: 'inline-block',
            width: 6,
            height: 18,
            borderRadius: 2,
            backgroundColor: '#0ea5e9',
            marginRight: 10,
          },
        }}
      >
        Missing Documents
      </Typography>

      <Card elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-end" sx={{ '& label, & .MuiFormLabel-root': { fontSize: 13 } }}>
            {!shouldHideOrgSelector ? (
              <Autocomplete
                size="small"
                sx={{ minWidth: 300 }}
                options={orgOptions}
                loading={orgLoading}
                getOptionLabel={(opt) => opt?.name || ''}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                value={orgOptions.find((o) => o.id === organizationId) || null}
                onChange={(_e, val) => setOrganizationId(val?.id || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Organization"
                    placeholder="Select organization"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {orgLoading ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            ) : null}

            <FormControl sx={{ minWidth: 260 }} size="small">
              <InputLabel id="types-label">Document Type</InputLabel>
              <Select
                labelId="types-label"
                value={selectedType}
                label="Document Type"
                onChange={handleTypesChange}
              >
                {documentTypes.map((t) => (
                  <MenuItem key={t} value={t}>{labelForType(t)}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              color="secondary"
              startIcon={exporting ? <CircularProgress size={18} /> : <DownloadIcon />}
              onClick={handleExport}
              disabled={exporting}
              size="small"
              sx={{
                whiteSpace: 'nowrap',
                textTransform: 'none',
                fontSize: 12,
                fontWeight: 600,
                color: (theme) => theme.palette.mode === 'dark' ? theme.palette.secondary.light : '#1f2937',
                borderColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.secondary.light : '#94a3b8',
                '&:hover': {
                  borderColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.secondary.main : '#64748b',
                },
                '&.Mui-disabled': {
                  color: '#64748b',
                  borderColor: '#cbd5e1',
                  opacity: 1,
                },
                '& .MuiButton-startIcon': {
                  color: 'inherit',
                },
              }}
            >
              Export CSV
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              startIcon={<EmailIcon />}
              onClick={() => setEmailOpen(true)}
              size="small"
              sx={{
                whiteSpace: 'nowrap',
                textTransform: 'none',
                fontSize: 12,
                fontWeight: 600,
                color: (theme) => theme.palette.mode === 'dark' ? theme.palette.secondary.light : '#1f2937',
                borderColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.secondary.light : '#94a3b8',
                '&:hover': {
                  borderColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.secondary.main : '#64748b',
                },
              }}
            >
              Email Report
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {summary && (
        <Card elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2 }}>
          <CardContent>
            <Stack spacing={2}>
              {/* First row: Always show 3 main tiles */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 2,
                }}
              >
                <SummaryTile
                  title="Total Employees"
                  value={summary.totalEmployees || 0}
                  color="#1976d2"
                />
                <SummaryTile
                  title="Employees Missing Docs"
                  value={summary.totalEmployeesMissingDocuments || 0}
                  color="#ed6c02"
                />
                <SummaryTile
                  title="Total Missing Documents"
                  value={summary.totalMissingDocuments || 0}
                  color="#d32f2f"
                />
              </Box>
              
              {/* Second row: Document type tiles (only if more than one type) */}
              {summary.missingByType && Object.keys(summary.missingByType).length > 1 && (() => {
                const docTypeTiles = [];
                if (summary.missingByType?.passport_copy !== undefined) {
                  docTypeTiles.push(
                    <SummaryTile
                      key="passport"
                      title="Passport"
                      value={summary.missingByType.passport_copy}
                      color="#9c27b0"
                    />
                  );
                }
                if (summary.missingByType?.visa_page !== undefined) {
                  docTypeTiles.push(
                    <SummaryTile
                      key="visa"
                      title="Visa"
                      value={summary.missingByType.visa_page}
                      color="#2e7d32"
                    />
                  );
                }
                if (summary.missingByType?.emirates_id !== undefined) {
                  docTypeTiles.push(
                    <SummaryTile
                      key="emirates"
                      title="Emirates ID"
                      value={summary.missingByType.emirates_id}
                      color="#0288d1"
                    />
                  );
                }
                if (summary.missingByType?.photo !== undefined) {
                  docTypeTiles.push(
                    <SummaryTile
                      key="photo"
                      title="Photo"
                      value={summary.missingByType.photo}
                      color="#f57c00"
                    />
                  );
                }
                if (summary.missingByType?.ohc !== undefined) {
                  docTypeTiles.push(
                    <SummaryTile
                      key="ohc"
                      title="OHC"
                      value={summary.missingByType.ohc}
                      color="#00796b"
                    />
                  );
                }
                if (summary.missingByType?.driving_license !== undefined) {
                  docTypeTiles.push(
                    <SummaryTile
                      key="driving"
                      title="Driving License"
                      value={summary.missingByType.driving_license}
                      color="#5d4037"
                    />
                  );
                }
                
                const tileCount = docTypeTiles.length;
                return (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${tileCount}, 1fr)`,
                      gap: 2,
                    }}
                  >
                    {docTypeTiles}
                  </Box>
                );
              })()}
            </Stack>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {['Employee Name', 'Employee ID', 'Organization', 'Document Type'].map((h) => (
                    <th key={h} style={{ textAlign: 'left', borderBottom: '1px solid #eee', padding: '8px', fontSize: 13, fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: '40px' }}>
                      <CircularProgress size={32} />
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                      No missing documents found
                    </td>
                  </tr>
                ) : (
                  rows.map((r, idx) => (
                    <tr key={idx}>
                      <td style={{ padding: '8px', borderBottom: '1px solid #f5f5f5', fontSize: 13 }}>
                        {[r.firstName, r.lastName].filter(Boolean).join(' ')}
                      </td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #f5f5f5', fontSize: 13 }}>{r.employeeId}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #f5f5f5', fontSize: 13 }}>{r.organizationName}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid #f5f5f5', fontSize: 13 }}>{r.missingDocumentType}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Box>

          {!loading && rows.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 2, borderTop: '1px solid #e5e7eb' }}>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Showing {rows.length} of {totalElements} results
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  size="small"
                  disabled={rows.length < size}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Email Report Dialog */}
      <Dialog open={emailOpen} onClose={() => setEmailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Missing Documents Report</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Typography variant="body2">
              Report will be generated from current filters.
            </Typography>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Organization Admins</Typography>
              {adminLoading ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={16} />
                  <Typography variant="caption">Loading admins...</Typography>
                </Stack>
              ) : (
                <Stack spacing={0.5}>
                  {adminOptions.length === 0 ? (
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      No admins found for this organization.
                    </Typography>
                  ) : (
                    <>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={adminOptions.length > 0 && selectedAdminIds.size === adminOptions.length}
                            indeterminate={selectedAdminIds.size > 0 && selectedAdminIds.size < adminOptions.length}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setSelectedAdminIds(checked ? new Set(adminOptions.map(a => a.id)) : new Set());
                            }}
                          />
                        }
                        label={<Typography variant="caption">Select all</Typography>}
                      />
                      {adminOptions.map((a) => (
                        <FormControlLabel
                          key={a.id}
                          control={
                            <Checkbox
                              size="small"
                              checked={selectedAdminIds.has(a.id)}
                              onChange={() => toggleAdminSelection(a.id)}
                            />
                          }
                          label={
                            <Typography variant="body2">
                              {a.name}{' '}
                              <Typography component="span" variant="caption" sx={{ color: '#555' }}>
                                ({a.email})
                              </Typography>
                            </Typography>
                          }
                        />
                      ))}
                    </>
                  )}
                </Stack>
              )}
            </Box>
            <TextField
              label="Additional Recipients (comma-separated emails)"
              fullWidth
              value={emailRecipients}
              onChange={(e) => setEmailRecipients(e.target.value)}
              placeholder="admin@example.com, ops@example.com"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailOpen(false)} disabled={sendingEmail}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEmailReport}
            disabled={sendingEmail || (selectedAdminIds.size === 0 && !emailRecipients.trim())}
            startIcon={sendingEmail ? <CircularProgress size={16} /> : <EmailIcon />}
          >
            {sendingEmail ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
